import type { ReportColumn } from '../models/column.types'
import type { Ga4DayEntry, ReportResponsePayload, ReportRow } from '../models/report.types'

// Date-only budget-allocation responses carry `data_ga4` (ORP-25549) and `total_ga4`: GA4's own
// values, where each session is counted once. Every column flagged `ga4_total_override: true`
// takes its value from them (ORP-25673); every other field stays as `data` / `total` returned it.
// Ports v3 `OrphexDataVisible/ga4Override.js`.

const DAY_MS = 24 * 3600 * 1000
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/u

export interface Ga4OverrideRowsResult {
  rows: ReportRow[]
  applied: boolean
  /** Override fields that took at least one GA4 value. */
  fields: string[]
}

export interface Ga4OverrideTotalResult<T> {
  total: T
  applied: boolean
  fields: string[]
}

export function dayKey(value: unknown): string | null {
  return value == null ? null : String(value).slice(0, 10)
}

function toUtcMs(value: unknown): number {
  const key = dayKey(value)
  if (!key || !DAY_KEY.test(key)) return Number.NaN
  return Date.parse(`${key}T00:00:00Z`)
}

/**
 * Days between the main range start and the comparison range start, read from the report's post
 * params. Row `date` + offset is the baseline day whose value sits in `prev_*`.
 */
export function getCompareOffsetDays(postData?: string | null): number | null {
  const params = new URLSearchParams(postData ?? '')
  const start = toUtcMs(params.get('date_start'))
  const compareStart = toUtcMs(params.get('compare_date_start'))
  if (!Number.isFinite(start) || !Number.isFinite(compareStart)) return null
  return Math.round((compareStart - start) / DAY_MS)
}

function shiftDay(key: string, offsetDays: number): string | null {
  const ms = toUtcMs(key)
  if (!Number.isFinite(ms)) return null
  return new Date(ms + offsetDays * DAY_MS).toISOString().slice(0, 10)
}

type Ga4Column = Pick<ReportColumn, 'field' | 'ga4_total_override'> | null | undefined

/**
 * Fields whose values GA4 overrides: every column flagged `ga4_total_override: true`. `columns` is
 * a field → column map or an array of columns.
 */
export function getGa4OverrideFields(
  columns?: Readonly<Record<string, Ga4Column>> | readonly Ga4Column[] | null,
): string[] {
  const list: readonly Ga4Column[] = Array.isArray(columns) ? columns : Object.values(columns ?? {})
  const flagged = list.flatMap((column) =>
    column?.ga4_total_override === true && column.field ? [column.field] : [],
  )
  return [...new Set(flagged)]
}

/**
 * The override fields of one response: every field GA4 sent back (`total_ga4` keys, `prev_*`
 * folded onto their base, and `data_ga4` day keys) whose catalog column carries the flag.
 */
export function resolveGa4OverrideFields(
  payload: Pick<ReportResponsePayload, 'data_ga4' | 'total_ga4'> | null | undefined,
  getColumn: (field: string) => Ga4Column,
): string[] {
  const candidates = new Set<string>()
  for (const key of Object.keys(payload?.total_ga4 ?? {}))
    candidates.add(key.replace(/^prev_/u, ''))
  for (const entry of Array.isArray(payload?.data_ga4) ? payload.data_ga4 : []) {
    for (const key of Object.keys(entry ?? {})) if (key !== 'date') candidates.add(key)
  }
  return getGa4OverrideFields([...candidates].map((field) => getColumn(field)))
}

function isFiniteValue(value: unknown): boolean {
  return value != null && value !== '' && Number.isFinite(Number(value))
}

/** `data_ga4` as a `YYYY-MM-DD` → { field: value } map; empty when the field is missing or unusable. */
export function buildGa4ByDay(
  dataGa4: readonly Ga4DayEntry[] | null | undefined,
  fields: readonly string[] = [],
): Map<string, Record<string, number>> {
  const byDay = new Map<string, Record<string, number>>()
  if (!Array.isArray(dataGa4)) return byDay
  for (const entry of dataGa4) {
    const key = dayKey(entry?.date)
    if (!key) continue
    const values: Record<string, number> = {}
    for (const field of fields) {
      if (isFiniteValue(entry[field])) values[field] = Number(entry[field])
    }
    if (Object.keys(values).length) byDay.set(key, { ...byDay.get(key), ...values })
  }
  return byDay
}

/**
 * Replaces every GA4 override field (and its `prev_*` twin when the row carries one and a compare
 * offset is known) with the matching `data_ga4` day. Days or fields missing from `data_ga4` keep
 * their original value. Returns new row objects plus the fields that took at least one GA4 value.
 */
export function applyGa4Override(
  rows: readonly ReportRow[],
  dataGa4?: readonly Ga4DayEntry[] | null,
  {
    compareOffsetDays = null,
    fields = [],
  }: { compareOffsetDays?: number | null; fields?: readonly string[] } = {},
): Ga4OverrideRowsResult {
  const byDay = buildGa4ByDay(dataGa4, fields)
  if (!byDay.size) return { rows: [...rows], applied: false, fields: [] }

  const appliedFields = new Set<string>()
  const nextRows = rows.map((row) => {
    const key = dayKey(row.date)
    if (!key) return row
    const current = byDay.get(key)
    const baselineKey =
      compareOffsetDays !== null && Number.isFinite(compareOffsetDays)
        ? shiftDay(key, compareOffsetDays)
        : null
    const baseline = baselineKey ? byDay.get(baselineKey) : undefined
    let next = row
    for (const field of fields) {
      const prevField = `prev_${field}`
      if (current && field in current && field in row) {
        next = { ...next, [field]: current[field] }
        appliedFields.add(field)
      }
      if (baseline && field in baseline && prevField in row) {
        next = { ...next, [prevField]: baseline[field] }
        appliedFields.add(field)
      }
    }
    return next
  })

  return { rows: nextRows, applied: appliedFields.size > 0, fields: [...appliedFields] }
}

// Sum of `field` over the `data_ga4` days inside [start, end] (inclusive); null when no day has it.
function sumGa4Range(
  byDay: Map<string, Record<string, number>>,
  field: string,
  start: string | null,
  end: string | null,
): number | null {
  const from = toUtcMs(start)
  const to = toUtcMs(end)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null
  let sum: number | null = null
  byDay.forEach((values, key) => {
    const ms = toUtcMs(key)
    const value = values[field]
    if (ms >= from && ms <= to && value !== undefined) sum = (sum ?? 0) + value
  })
  return sum
}

export interface Ga4OverrideTotalOptions {
  totalGa4?: Readonly<Record<string, unknown>> | null | undefined
  dataGa4?: readonly Ga4DayEntry[] | null | undefined
  postData?: string | null | undefined
  fields?: readonly string[]
}

/**
 * Replaces every GA4 override field (and its `prev_*` twin) of a response total with GA4's own
 * value. Per field, `total_ga4` wins when it carries the field; otherwise the `data_ga4` days inside
 * the main (and compare) date range of `postData` are summed. Fields the total doesn't carry are
 * left out.
 */
export function applyGa4OverrideTotal<
  T extends Readonly<Record<string, unknown>> | null | undefined,
>(
  total: T,
  { totalGa4, dataGa4, postData, fields = [] }: Ga4OverrideTotalOptions = {},
): Ga4OverrideTotalResult<T> {
  if (!total || typeof total !== 'object') return { total, applied: false, fields: [] }

  const params = new URLSearchParams(postData ?? '')
  let byDay: Map<string, Record<string, number>> | null = null
  let next: Record<string, unknown> = total
  const appliedFields = new Set<string>()

  for (const field of fields) {
    const prevField = `prev_${field}`
    let current: number | null
    let previous: number | null = null

    if (isFiniteValue(totalGa4?.[field])) {
      current = Number(totalGa4?.[field])
      if (isFiniteValue(totalGa4?.[prevField])) previous = Number(totalGa4?.[prevField])
    } else {
      byDay ??= buildGa4ByDay(dataGa4, fields)
      if (!byDay.size) continue
      current = sumGa4Range(byDay, field, params.get('date_start'), params.get('date_end'))
      previous = sumGa4Range(
        byDay,
        field,
        params.get('compare_date_start'),
        params.get('compare_date_end'),
      )
    }

    if (current !== null && field in total) {
      next = { ...next, [field]: current }
      appliedFields.add(field)
    }
    if (previous !== null && prevField in total) {
      next = { ...next, [prevField]: previous }
      appliedFields.add(field)
    }
  }

  return { total: next as T, applied: appliedFields.size > 0, fields: [...appliedFields] }
}

/**
 * Marks the columns whose values took GA4 data: `ga4Override` drives the orange dot, and the GA4
 * explanation replaces the header tooltip (v3 `OrphexTh` shows the GA4 tooltip instead of the
 * column description).
 */
export function markGa4Columns(
  columns: readonly ReportColumn[],
  fields: readonly string[],
  description: string,
): ReportColumn[] {
  if (!fields.length) return columns.map((column) => ({ ...column }))
  return columns.map((column) =>
    fields.includes(column.field) ? { ...column, ga4Override: true, description } : { ...column },
  )
}
